import React, { useEffect, useState } from "react";
import { message, Divider, Typography } from "antd";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getApkConfig,
  putApkConfig,
  selectApkConfig,
  selectApkError,
  selectApkLoading,
} from "./apkSlice";
import {
  KlearlyAPKConfig,
  KlearlyAPKConfigVersion,
} from "../../interfaces/klearlyApkConfig.interface";
import GeneralTableHeader from "../common/general-table-header/GeneralTableHeader";
import { SettingOutlined } from "@ant-design/icons";
import ApkVersionsGrid from "./components/ApkVersionsGrid/ApkVersionsGrid";
import ApkVersionEditorModal from "./components/ApkVersionEditorModal/ApkVersionEditorModal";
import RapydCoreGrid from "./components/RapydCoreGrid/RapydCoreGrid";
import RapydCoreEditorModal from "./components/RapydCoreEditorModal/RapydCoreEditorModal";

type VersionKey =
  | "fullVersion"
  | "liteVersion"
  | "liteSandboxVersion"
  | "iOSVersion"
  | "liteVersionPax"
  | "liteVersionSoftpay";

export default function Development() {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectApkLoading);
  const error = useAppSelector(selectApkError);
  const config = useAppSelector(selectApkConfig);
  const [editingKey, setEditingKey] = useState<VersionKey | null>(null);
  const [editingDepKey, setEditingDepKey] = useState<
    keyof KlearlyAPKConfig["rapydCoreVersion"] | null
  >(null);

  useEffect(() => {
    dispatch(getApkConfig());
  }, [dispatch]);

  useEffect(() => {
    if (error) message.error(error);
  }, [error]);

  const onSave = async (
    key: VersionKey,
    values: KlearlyAPKConfigVersion & { metadata_rapydCoreVersion?: string }
  ) => {
    const { metadata_rapydCoreVersion, ...rest } = values as any;
    const payload: Partial<KlearlyAPKConfig> = {
      [key]: {
        ...rest,
        metadata: metadata_rapydCoreVersion
          ? { rapydCoreVersion: metadata_rapydCoreVersion }
          : rest.metadata,
      } as KlearlyAPKConfigVersion,
    } as Partial<KlearlyAPKConfig>;

    try {
      await dispatch(putApkConfig(payload)).unwrap();
      message.success(`${key} updated`);
      setEditingKey(null);
    } catch (e) {
      // handled by rejected case
    }
  };

  const onSaveRapyd = async (
    depKey: keyof KlearlyAPKConfig["rapydCoreVersion"],
    values: { name: string }
  ) => {
    const current =
      config?.rapydCoreVersion ?? ({} as KlearlyAPKConfig["rapydCoreVersion"]);
    const payload: Partial<KlearlyAPKConfig> = {
      rapydCoreVersion: {
        ...current,
        [depKey]: {
          name: values.name,
        },
      },
    } as Partial<KlearlyAPKConfig>;

    try {
      await dispatch(putApkConfig(payload)).unwrap();
      message.success(`${depKey} updated`);
      setEditingDepKey(null);
    } catch (e) {
      // handled by rejected case
    }
  };

  const version = editingKey ? config?.[editingKey] : undefined;
  const rapydDep = editingDepKey
    ? config?.rapydCoreVersion?.[editingDepKey]
    : undefined;

  return (
    <div style={{ padding: 16 }}>
      <GeneralTableHeader
        title="Development"
        description="Klearly APK configurations"
        avatarUrl={<SettingOutlined style={{ fontSize: 30 }} />}
      />
      <Typography.Title level={4} style={{ margin: "16px 0 16px" }}>
        Klearly Versions
      </Typography.Title>
      <ApkVersionsGrid
        config={config}
        loading={loading}
        onEdit={(key) => setEditingKey(key)}
      />

      <Divider style={{ margin: "24px 0" }} />
      <Typography.Title level={4} style={{ margin: "16px 0 16px" }}>
        Rapyd Core Versions
      </Typography.Title>

      <RapydCoreGrid
        config={config?.rapydCoreVersion}
        loading={loading}
        onEdit={(key) => setEditingDepKey(key)}
      />

      <ApkVersionEditorModal
        open={!!editingKey}
        versionKey={editingKey}
        version={version}
        loading={loading}
        onCancel={() => setEditingKey(null)}
        onSave={(vals) => editingKey && onSave(editingKey, vals)}
      />

      <RapydCoreEditorModal
        open={!!editingDepKey}
        depKey={editingDepKey}
        dependency={rapydDep}
        loading={loading}
        onCancel={() => setEditingDepKey(null)}
        onSave={(vals) => editingDepKey && onSaveRapyd(editingDepKey, vals)}
      />
    </div>
  );
}
